/**
 * Osgoode Marley: newsletter signup tracking
 * ------------------------------------------------------------------------------
 * Publishes ONE Shopify custom customer event for every successful newsletter
 * signup, from both native Shopify forms and Klaviyo forms:
 *
 *   Shopify.analytics.publish('om:newsletter_signup', {
 *     source:   'shopify' | 'klaviyo',
 *     form_id:  originating form identifier,
 *     event_id: unique id per submission (used by Meta for deduplication)
 *   });
 *
 * A Shopify Custom Pixel (meta-custom-pixel/newsletter-lead-pixel.js, pasted into
 * Admin > Settings > Customer events) subscribes to this event and sends Meta `Lead`.
 * Never put email addresses or other PII in the payload.
 *
 * Native Shopify forms (non-AJAX, page reloads after POST):
 *   1. Newsletter forms contain a marker rendered by snippets/om-newsletter-marker.liquid.
 *   2. On submit, a single pending entry {form_id, event_id, ts} is written to
 *      sessionStorage (each new submit overwrites it).
 *   3. On the next page showing that form's marker in `success` state
 *      (form.posted_successfully?), and with a matching #hash when one is present,
 *      the entry is consumed and the event is published once. Refreshing the
 *      success page finds no entry, so nothing is re-sent.
 *
 * Klaviyo forms: one listener for Klaviyo's `klaviyoForms` window event.
 */
(function () {
  'use strict';

  if (window.omNewsletterTracking) return; // guard against the script being included twice
  window.omNewsletterTracking = true;

  // Don't track from the theme editor.
  if (window.Shopify && window.Shopify.designMode) return;

  /* --------------------------------------------------------------------------
   * CONFIGURATION: Klaviyo form allowlist
   *
   * Klaviyo form IDs (strings) to track as newsletter signups.
   *   - Empty array: track ANY Klaviyo form submit that includes an email address.
   *   - Non-empty:   track ONLY submits from the listed forms.
   *
   * Find a form's ID in Klaviyo > Sign-up forms > (open form): it's the short
   * code in the editor URL, e.g. https://www.klaviyo.com/forms/<FORM_ID>/edit
   * Example: KLAVIYO_FORM_IDS = ['AbC123', 'XyZ789'];
   * ------------------------------------------------------------------------ */
  var KLAVIYO_FORM_IDS = [];

  var EVENT_NAME = 'om:newsletter_signup';
  var PENDING_KEY = 'om_newsletter_pending';
  // 15 minutes: covers the time between submit and the success response,
  // including an hCaptcha challenge page. Older pending entries are discarded.
  var PENDING_MAX_AGE_MS = 15 * 60 * 1000;

  function uniqueId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12) + Math.random().toString(36).slice(2, 12);
  }

  function publish(source, formId, eventId) {
    var analytics = window.Shopify && window.Shopify.analytics;
    if (!analytics || typeof analytics.publish !== 'function') return;
    try {
      analytics.publish(EVENT_NAME, { source: source, form_id: formId, event_id: eventId });
    } catch (e) {}
  }

  function readPending() {
    try {
      var raw = window.sessionStorage.getItem(PENDING_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writePending(value) {
    try {
      window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(value));
    } catch (e) {}
  }

  function clearPending() {
    try {
      window.sessionStorage.removeItem(PENDING_KEY);
    } catch (e) {}
  }

  /* --- Native Shopify newsletter forms ------------------------------------ */

  // Capture phase so this runs before other submit handlers (e.g. hCaptcha).
  // Only marks the attempt; nothing is published until the success response.
  document.addEventListener('submit', function (evt) {
    var form = evt.target;
    if (!form || form.tagName !== 'FORM') return;
    var marker = form.querySelector('[data-om-newsletter-form]');
    if (!marker) return;
    writePending({
      form_id: marker.getAttribute('data-om-newsletter-form'),
      event_id: uniqueId(),
      ts: Date.now()
    });
  }, true);

  function checkNativeSuccess() {
    var pending = readPending();
    if (!pending) return;

    var age = Date.now() - pending.ts;
    if (!pending.form_id || !pending.event_id || !(age >= 0 && age <= PENDING_MAX_AGE_MS)) {
      clearPending();
      return;
    }

    var markers = document.querySelectorAll('[data-om-newsletter-form]');
    var match = null;
    for (var i = 0; i < markers.length; i++) {
      if (markers[i].getAttribute('data-om-newsletter-form') === pending.form_id) {
        match = markers[i];
        break;
      }
    }
    // Form not on this page, or shown in its normal state (e.g. an hCaptcha
    // challenge page): keep the entry until the response arrives or it expires.
    if (!match) return;

    var state = match.getAttribute('data-om-newsletter-state');
    if (state === 'error') {
      clearPending();
      return;
    }
    if (state !== 'success') return;

    // posted_successfully? is true for every customer form on the page, so when
    // Shopify's #<form-id> hash is present it must name the pending form.
    var hash = window.location.hash ? window.location.hash.slice(1) : '';
    try { hash = decodeURIComponent(hash); } catch (e) {}
    clearPending();
    if (hash && hash !== pending.form_id) return;

    publish('shopify', pending.form_id, pending.event_id);
  }

  checkNativeSuccess();

  /* --- Klaviyo forms ------------------------------------------------------ */
  // https://developers.klaviyo.com/en/v1-2/docs/track-klaviyo-form-activity-using-javascript

  var klaviyoTracked = {}; // formId -> true, once per page view (multistep forms)

  window.addEventListener('klaviyoForms', function (evt) {
    var detail = evt && evt.detail;
    if (!detail || detail.type !== 'submit') return;

    var formId = detail.formId ? String(detail.formId) : '';
    if (!formId || klaviyoTracked[formId]) return;
    if (KLAVIYO_FORM_IDS.length && KLAVIYO_FORM_IDS.indexOf(formId) === -1) return;

    // Require an email address (phone-only/SMS submits are ignored). The value
    // is only checked here; it is never stored or published.
    var meta = detail.metaData || {};
    var email = meta.$email || meta.email;
    if (typeof email !== 'string' || email.indexOf('@') < 1) return;

    klaviyoTracked[formId] = true;
    publish('klaviyo', formId, uniqueId());
  });
})();
